import { useState } from 'react';
import { Button } from '@jywer/ui';
import {
  addTemplateItem,
  deleteTemplateItem,
  updateTemplateItem,
  updateTemplateName,
  type ChecklistTemplate,
} from './api';

interface TemplateEditorProps {
  template: ChecklistTemplate;
  onChanged: () => Promise<void>;
}

export function TemplateEditor({ template, onChanged }: TemplateEditorProps) {
  const [name, setName] = useState(template.name);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [newItemText, setNewItemText] = useState('');
  const [pendingPropagate, setPendingPropagate] = useState<string | null>(null);

  const run = async (op: () => Promise<unknown>) => {
    setBusy(true);
    setErr(null);
    try {
      await op();
      await onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const saveName = () => {
    if (name === template.name) return;
    if (name.trim() === '') {
      setName(template.name);
      return;
    }
    void run(() => updateTemplateName(template.id, name.trim()));
  };

  const proposeAddItem = () => {
    if (newItemText.trim() === '') return;
    if (template.active_instance_count > 0) {
      setPendingPropagate(newItemText.trim());
      return;
    }
    void run(async () => {
      await addTemplateItem(template.id, { text: newItemText.trim() });
      setNewItemText('');
    });
  };

  const confirmPropagate = () => {
    if (!pendingPropagate) return;
    void run(async () => {
      await addTemplateItem(template.id, { text: pendingPropagate });
      setNewItemText('');
      setPendingPropagate(null);
    });
  };

  return (
    <div className="flex flex-col gap-3 rounded border border-line-1 bg-paper-0 p-4">
      <div className="flex items-center justify-between gap-3">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={saveName}
          onKeyDown={(e) => {
            if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
          }}
          disabled={busy}
          className="flex-1 rounded-token-md border border-transparent bg-transparent px-1 text-h3 text-ink-0 hover:border-line-2 focus:border-primary focus:bg-paper-1 focus:outline-none"
        />
        <span className="rounded-token-pill bg-paper-2 px-2 py-0.5 text-meta text-ink-2">
          {template.phase}
        </span>
      </div>

      <div className="text-meta text-ink-3">
        {template.active_instance_count > 0 ? (
          <>Bruges af {template.active_instance_count} aktive projekt-instanser.</>
        ) : (
          <>Ingen aktive instanser endnu.</>
        )}
      </div>

      {err ? (
        <div className="rounded-token-md border border-status-err/40 bg-status-err-tint px-3 py-2 text-meta text-status-err">
          {err}
        </div>
      ) : null}

      <ul className="flex flex-col gap-1">
        {template.items.length === 0 ? (
          <li className="text-meta italic text-ink-3">Ingen punkter endnu.</li>
        ) : (
          template.items.map((it) => (
            <TemplateItemRow
              key={it.id}
              templateId={template.id}
              item={it}
              busy={busy}
              onChanged={onChanged}
              setErr={setErr}
              setBusy={setBusy}
            />
          ))
        )}
      </ul>

      <div className="flex flex-col gap-2 rounded-token-md border border-line-1 bg-paper-1 p-2">
        <input
          type="text"
          value={newItemText}
          onChange={(e) => setNewItemText(e.target.value)}
          placeholder="Nyt punkt"
          disabled={busy}
          className="rounded-token-md border border-line-2 bg-paper-0 px-2 py-1 text-ui text-ink-0"
          onKeyDown={(e) => {
            if (e.key === 'Enter') proposeAddItem();
          }}
        />
        {pendingPropagate ? (
          <div className="flex flex-col gap-2 rounded-token-md border border-status-warn/40 bg-status-warn-tint px-3 py-2 text-meta">
            <span className="text-ink-1">
              Tilføjes punktet, vil det også blive tilføjet til{' '}
              <strong>{template.active_instance_count}</strong> aktive projekt-instans
              {template.active_instance_count === 1 ? '' : 'er'}. Bekræft for at fortsætte.
            </span>
            <div className="flex items-center justify-end gap-2">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setPendingPropagate(null)}
                disabled={busy}
              >
                Annullér
              </Button>
              <Button size="sm" onClick={confirmPropagate} disabled={busy}>
                Tilføj og propager
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-end">
            <Button size="sm" onClick={proposeAddItem} disabled={busy || newItemText.trim() === ''}>
              + Tilføj punkt
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function TemplateItemRow({
  templateId,
  item,
  busy,
  onChanged,
  setErr,
  setBusy,
}: {
  templateId: string;
  item: { id: string; text: string };
  busy: boolean;
  onChanged: () => Promise<void>;
  setErr: (s: string | null) => void;
  setBusy: (b: boolean) => void;
}) {
  const [text, setText] = useState(item.text);
  const run = async (op: () => Promise<unknown>) => {
    setBusy(true);
    setErr(null);
    try {
      await op();
      await onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="flex items-center gap-2 rounded-token-md border border-line-1 bg-paper-1 px-2 py-1">
      <input
        type="text"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={() => {
          if (text === item.text) return;
          if (text.trim() === '') {
            setText(item.text);
            return;
          }
          void run(() => updateTemplateItem(templateId, item.id, text.trim()));
        }}
        disabled={busy}
        className="flex-1 rounded-token-md border border-transparent bg-transparent px-1 text-ui text-ink-0 hover:border-line-2 focus:border-primary focus:bg-paper-0 focus:outline-none"
      />
      <button
        type="button"
        disabled={busy}
        onClick={() => {
          if (!confirm(`Slet "${item.text}"?`)) return;
          void run(() => deleteTemplateItem(templateId, item.id));
        }}
        className="text-meta text-ink-3 hover:text-status-err"
        aria-label={`Slet ${item.text}`}
      >
        ✕
      </button>
    </li>
  );
}
